import { useState } from "react";
import {
  Archive,
  Reply,
  ChevronRight,
  ClipboardCheck,
  BarChart3,
  FileText,
  Flame,
  Folder as FolderIcon,
  Inbox,
  ListFilter,
  LogOut,
  Mail,
  MailOpen,
  OctagonAlert,
  PenLine,
  Receipt,
  Send,
  Settings,
  Sparkles,
  SquarePen,
  Trash2,
  X,
  type LucideIcon,
} from "lucide-react";
import type { Account, Folder } from "../api";
import { ordnerName, ordnerRang } from "../lib/ordner";
import { ThemeToggle } from "./ThemeToggle";
import { OrdnerAktionen } from "./OrdnerAktionen";

export type View =
  | { kind: "folder"; folderId: number; label: string }
  | { kind: "smart"; id: "antworten" | "needsAction" | "unread" | "hoch" | "drafts"; label: string }
  | { kind: "category"; category: string; label: string }
  | { kind: "page"; id: "rules" | "stats" | "belege" | "einrichtung" | "aufgaben"; label: string };

interface Props {
  accounts: Account[];
  folders: Folder[];
  view: View;
  pendingCount: number;
  /** Mails, die eine Antwort von dir erwarten. */
  antwortenCount?: number;
  draftCount: number;
  /** Offene Aufgaben, etwa „Kundenordner anlegen?". */
  aufgabenCount?: number;
  /** Ungelesene im Posteingang samt Kundenordnern — dieselbe Zahl wie am App-Icon. */
  ungelesenCount?: number;
  onSelect: (view: View) => void;
  onLogout: () => void;
  /** Nach Anlegen, Umbenennen, Verschieben oder Löschen eines Ordners. */
  onFoldersChanged: () => void;
  onCompose: () => void;
  /** Nur für schmale Fenster relevant: dort ist immer genau eine Spalte sichtbar. */
  active: boolean;
  /** Schmale Fenster: Navigation schließen, ohne etwas zu wählen. */
  onClose?: () => void;
}

const ZU_KEY = "maildeck-ordner-zu";

function gespeichertZu(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(ZU_KEY) ?? "[]") as string[]);
  } catch {
    return new Set();
  }
}

/** Pfad in seine Ebenen zerlegen — Vergleich Ebene für Ebene hält Unterordner unter ihrem Elternordner. */
const ebenen = (f: Folder) => (f.delimiter ? f.path.split(f.delimiter) : [f.path]);

/** Schlüssel für den Klappzustand: pro Konto, sonst klappen gleichnamige Ordner zweier Postfächer gemeinsam. */
const zuSchluessel = (accountId: number, path: string) => `${accountId}:${path}`;

const SPECIAL_ICON: Record<string, LucideIcon> = {
  "\\Inbox": Inbox,
  "\\Sent": Send,
  "\\Drafts": FileText,
  "\\Archive": Archive,
  "\\Junk": OctagonAlert,
  "\\Trash": Trash2,
};

/** Unterordner eingerückt: „Kunden.System4" steht unter „Kunden". */
function tiefeVon(f: Folder): number {
  if (!f.delimiter) return 0;
  return f.path.split(f.delimiter).length - 1;
}

function folderIcon(f: Folder): LucideIcon {
  if (f.special_use && SPECIAL_ICON[f.special_use]) return SPECIAL_ICON[f.special_use]!;
  return f.path.toUpperCase() === "INBOX" ? Inbox : FolderIcon;
}

const START: View = { kind: "smart", id: "needsAction", label: "Vorschläge" };

export function Sidebar({ accounts, folders, view, pendingCount, antwortenCount = 0, draftCount, aufgabenCount = 0, ungelesenCount = 0, onSelect, onLogout, onFoldersChanged, onCompose, active, onClose }: Props) {
  const [zu, setZu] = useState<Set<string>>(gespeichertZu);

  const istZu = (f: Folder) => zu.has(zuSchluessel(f.account_id, f.path)) || zu.has(f.path);

  // Baumreihenfolge: zuerst nach dem obersten Ordner (Systemordner in fester
  // Folge), dann Ebene für Ebene. So stehen „Posteingang / Kunden / …" direkt
  // unter dem Posteingang statt hinter dem Papierkorb.
  const sortiert = (liste: Folder[]) =>
    [...liste].sort((a, b) => {
      const ra = ordnerRang(a, liste), rb = ordnerRang(b, liste);
      if (ra !== rb) return ra - rb;
      const ea = ebenen(a), eb = ebenen(b);
      for (let i = 0; i < Math.min(ea.length, eb.length); i++) {
        const c = ea[i]!.localeCompare(eb[i]!, "de", { sensitivity: "base" });
        if (c !== 0) return c;
      }
      return ea.length - eb.length;
    });

  const imKonto = (f: Folder) => folders.filter((g) => g.account_id === f.account_id);
  const unter = (f: Folder, g: Folder) => Boolean(f.delimiter) && g.account_id === f.account_id && g.path.startsWith(f.path + f.delimiter);
  const hatKinder = (f: Folder) => folders.some((g) => unter(f, g));
  // Versteckt, sobald irgendein Elternordner zugeklappt ist.
  const versteckt = (f: Folder) => {
    const e = ebenen(f);
    for (let i = 1; i < e.length; i++) {
      const pfad = e.slice(0, i).join(f.delimiter!);
      if (zu.has(zuSchluessel(f.account_id, pfad)) || zu.has(pfad)) return true;
    }
    return false;
  };
  /** Ungelesene eines zugeklappten Ordners samt allem darunter — sonst verschwänden sie aus dem Blick. */
  const ungelesenSumme = (f: Folder) =>
    imKonto(f).filter((g) => g.id === f.id || unter(f, g)).reduce((n, g) => n + (g.unseen || 0), 0);

  const umschalten = (f: Folder) => {
    setZu((s) => {
      const n = new Set(s);
      const k = zuSchluessel(f.account_id, f.path);
      // Alte Einträge ohne Konto (frühere Version) gleich mit aufräumen.
      if (n.has(k) || n.has(f.path)) { n.delete(k); n.delete(f.path); } else n.add(k);
      try { localStorage.setItem(ZU_KEY, JSON.stringify([...n])); } catch { /* privater Modus */ }
      return n;
    });
  };

  const isCurrent = (v: View) =>
    v.kind === view.kind &&
    ((v.kind === "folder" && view.kind === "folder" && v.folderId === view.folderId) ||
      (v.kind === "smart" && view.kind === "smart" && v.id === view.id) ||
      (v.kind === "category" && view.kind === "category" && v.category === view.category) ||
      (v.kind === "page" && view.kind === "page" && v.id === view.id));

  const zahl = (count: number | undefined, hot: boolean | undefined, was: string) =>
    count ? (
      <span className="count" data-hot={hot || undefined} aria-label={`${count} ${was}`}>
        {count > 999 ? "999+" : count}
      </span>
    ) : null;

  const item = (v: View, Icon: LucideIcon, count?: number, hot?: boolean, was = "") => (
    <button
      key={v.kind === "folder" ? `folder-${v.folderId}` : `${v.kind}-${v.label}`}
      type="button"
      className="nav-item"
      aria-current={isCurrent(v) ? "page" : undefined}
      onClick={() => onSelect(v)}
    >
      <Icon aria-hidden="true" />
      <span className="label">{v.label}</span>
      {zahl(count, hot, was)}
    </button>
  );

  const email = accounts[0]?.email ?? "";
  const mehrereKonten = accounts.length > 1;

  const baum = (liste: Folder[]) =>
    sortiert(liste).filter((f) => !versteckt(f)).map((f) => {
      const v: View = { kind: "folder", folderId: f.id, label: ordnerName(f) };
      const Icon = folderIcon(f);
      const kinder = hatKinder(f);
      const eingeklappt = kinder && istZu(f);
      const count = eingeklappt ? ungelesenSumme(f) : f.unseen;
      return (
        <div key={f.id} className="nav-row" style={{ paddingLeft: tiefeVon(f) * 14 }}>
          {kinder ? (
            <button
              type="button"
              className="nav-toggle"
              aria-expanded={!eingeklappt}
              aria-label={eingeklappt ? `${v.label} aufklappen` : `${v.label} zuklappen`}
              title={eingeklappt ? "Aufklappen" : "Zuklappen"}
              onClick={() => umschalten(f)}
            >
              <ChevronRight aria-hidden="true" data-offen={!eingeklappt} />
            </button>
          ) : (
            <span className="nav-toggle" aria-hidden="true" />
          )}
          <button
            type="button"
            className="nav-item"
            aria-current={isCurrent(v) ? "page" : undefined}
            onClick={() => onSelect(v)}
            title={f.path}
          >
            <Icon aria-hidden="true" />
            <span className="label">{v.label}</span>
            {zahl(count, false, eingeklappt ? "ungelesen, mit Unterordnern" : "ungelesen")}
          </button>
          <OrdnerAktionen
            folder={f}
            folders={folders}
            onChanged={onFoldersChanged}
            onDeleted={() => isCurrent(v) && onSelect(START)}
          />
        </div>
      );
    });

  return (
    <nav className="pane sidebar" data-active={active} aria-label="Navigation">
      <div className="brand">
        <img className="brand-mark" src="/favicon.svg" alt="" aria-hidden="true" />
        <span className="brand-text">
          <b>Maildeck</b>
          <small title={email}>{mehrereKonten ? `${accounts.length} Konten` : email || "kein Konto"}</small>
        </span>
        {onClose && (
          <button type="button" className="icon-btn only-narrow" onClick={onClose} aria-label="Navigation schließen" title="Schließen">
            <X aria-hidden="true" />
          </button>
        )}
      </div>

      <button type="button" className="btn primary neue-mail" onClick={onCompose} title="Neue Mail (C)">
        <SquarePen aria-hidden="true" /> Neue Mail
      </button>

      <div className="pane-scroll">
        <div className="nav-group">
          {item({ kind: "smart", id: "antworten", label: "Zu beantworten" }, Reply, antwortenCount, true, "Mails, die auf deine Antwort warten")}
          {item(START, Sparkles, pendingCount, true, "offene Vorschläge")}
          {item({ kind: "page", id: "aufgaben", label: "Aufgaben" }, ClipboardCheck, aufgabenCount, true, "offene Aufgaben")}
          {item({ kind: "smart", id: "hoch", label: "Dringend" }, Flame)}
          {item({ kind: "smart", id: "unread", label: "Ungelesen" }, MailOpen, ungelesenCount, false, "ungelesen im Posteingang")}
          {item({ kind: "smart", id: "drafts", label: "Entwürfe" }, PenLine, draftCount, false, "Entwürfe")}
        </div>

        {mehrereKonten ? (
          accounts.map((a) => {
            const eigene = folders.filter((f) => f.account_id === a.id);
            if (eigene.length === 0) return null;
            return (
              <div className="nav-group" key={a.id}>
                <h3 title={a.email}>{a.name || a.email}</h3>
                {baum(eigene)}
              </div>
            );
          })
        ) : (
          <div className="nav-group">
            <h3>Ordner</h3>
            {folders.length === 0 ? <p className="nav-leer">Noch keine Ordner synchronisiert.</p> : baum(folders)}
          </div>
        )}

        <div className="nav-group">
          <h3>Kategorien</h3>
          {(["beleg", "rechnung", "kunde", "lieferant", "termin", "bewerbung"] as const).map((c) => {
            const v: View = { kind: "category", category: c, label: c[0]!.toUpperCase() + c.slice(1) };
            return (
              <button
                key={c}
                type="button"
                className="nav-item"
                aria-current={isCurrent(v) ? "page" : undefined}
                onClick={() => onSelect(v)}
              >
                <span className="nav-swatch" style={{ color: `var(--cat-${c})` }} aria-hidden="true" />
                <span className="label">{v.label}</span>
              </button>
            );
          })}
        </div>

        <div className="nav-group">
          <h3>Agent</h3>
          {item({ kind: "page", id: "belege", label: "Belege & USt" }, Receipt)}
          {item({ kind: "page", id: "rules", label: "Regeln" }, ListFilter)}
          {item({ kind: "page", id: "stats", label: "Auswertung" }, BarChart3)}
          {item({ kind: "page", id: "einrichtung", label: "Einstellungen" }, Settings)}
        </div>
      </div>

      <div className="sidebar-foot">
        <div className="user-row">
          <span className="user-avatar" aria-hidden="true">{(email || "M").slice(0, 2).toUpperCase()}</span>
          <span className="user-text">
            <b title={email}>{email || "Maildeck"}</b>
            <small>{accounts.length === 1 ? "1 Konto" : `${accounts.length} Konten`}</small>
          </span>
          <button type="button" className="icon-btn" onClick={onLogout} title="Abmelden" aria-label="Abmelden">
            <LogOut aria-hidden="true" />
          </button>
        </div>
        <div className="user-row sidebar-darstellung">
          <span>Darstellung</span>
          <ThemeToggle />
        </div>
      </div>
    </nav>
  );
}
